import styles from "./FooterHint.module.css";

export interface FooterHintLabels {
  /** "dismiss" — follows the `E` keycap. */
  dismiss: string;
  /** "navigate" — follows the `J/K` keycap pair. */
  navigate: string;
  /**
   * Localized "or" separator announced between the `J` and `K`
   * keycaps. The visible glyph is `/`; this label is the audible
   * substitute (e.g. "J or K navigate") so screen readers don't
   * announce "J K navigate" without the pairing relationship.
   */
  orSeparator: string;
  /**
   * Shown in place of the keycaps when the panel lacks OS keyboard
   * focus, e.g. "Click panel to use keyboard". Chrome can't transfer
   * focus to an open side panel programmatically (PRD §2.1), so the
   * in-column keys are dead until the user clicks in — this line says so.
   * Optional: surfaces that never render the unfocused state (web, where a
   * normal tab owns focus and `focused` defaults to `true`) may omit it.
   */
  activatePrompt?: string;
}

export interface FooterHintProps {
  labels: FooterHintLabels;
  /**
   * Whether the host surface currently holds OS keyboard focus.
   * `true` (default) → keycap hints; `false` → the `activatePrompt`
   * line, because the keys won't fire without focus. Extension side
   * panel drives this from `useHasFocus()`; web omits it (always focused).
   */
  focused?: boolean;
}

/**
 * PRD docs/prd-keyboard.md §4.5 — keycap-styled keyboard hints at the
 * bottom of the column. Three keys: `E`, `J`, `K`. The leader chord
 * isn't shown here because it lives in onboarding (PRD §4.5 spec'd
 * only the in-column keys).
 */
export function FooterHint({ labels, focused = true }: FooterHintProps) {
  if (!focused) {
    // No prompt → render nothing, never a blank bordered strip.
    if (!labels.activatePrompt) return null;
    // Same `.footer` wrapper keeps single-row height; no aria-live (see FooterHint.test.tsx live-region test).
    return (
      <div className={styles.footer}>
        <span className={styles.prompt}>{labels.activatePrompt}</span>
      </div>
    );
  }
  return (
    // No landmark role and no aria-label — the keycap hints are
    // inline supplementary content; an extra `region` landmark
    // would clutter screen-reader landmark navigation in an
    // already narrow side panel. The keys are read in document
    // order ("E dismiss" then "J or K navigate") which conveys
    // the meaning without a wrapping landmark.
    <div className={styles.footer}>
      <kbd className={styles.key}>E</kbd>
      <span className={styles.text}>{labels.dismiss}</span>
      <span className={styles.dot} aria-hidden="true">
        ·
      </span>
      <kbd className={styles.key}>J</kbd>
      <span className={styles.slash} aria-hidden="true">
        /
      </span>
      {/* Visually-hidden "or" so screen readers announce the J/K
          pairing relationship — the visible `/` is aria-hidden. */}
      <span className={styles.srOnly}>{labels.orSeparator}</span>
      <kbd className={styles.key}>K</kbd>
      <span className={styles.text}>{labels.navigate}</span>
    </div>
  );
}
